import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shopService } from '../../services/shopService';
import { userService } from '../../services/userService';
import { Crown, Star, CheckCircle2, Gift, ArrowRight, ShieldCheck, Zap } from 'lucide-react';
import confetti from 'canvas-confetti';

const StampPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [stampData, setStampData] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    
    const shopId = searchParams.get('shopId') || sessionStorage.getItem('currentShopId');
    const userPhone = sessionStorage.getItem('userPhone');

    useEffect(() => {
        const fetchProgress = async () => {
            if (!shopId || !userPhone) {
                navigate(`/scan?shopId=${shopId || ''}`);
                return;
            }

            try {
                // Try to get shop from cache first
                let shop;
                const cachedShop = sessionStorage.getItem(`shop_${shopId}`);
                if (cachedShop) {
                    shop = JSON.parse(cachedShop);
                } else {
                    shop = await shopService.getShopById(shopId);
                    if (shop) sessionStorage.setItem(`shop_${shopId}`, JSON.stringify(shop));
                }

                const activeIdx = sessionStorage.getItem('currentCampaignIdx') || 0;
                const progress = await shopService.getStampProgress(shopId, activeIdx, userPhone);
                
                if (progress) {
                    // OPTIMISTIC UPDATE: Show next stamp immediately while saving in background
                    const lastScanTime = sessionStorage.getItem('lastStampTime');
                    const now = Date.now();
                    const isNewScan = !lastScanTime || now - parseInt(lastScanTime) > 30000;

                    if (isNewScan) {
                        // Update state immediately for "instant" feel
                        const optimisticCount = Math.min(progress.count + 1, progress.total);
                        setStampData({ ...progress, count: optimisticCount });
                        if (optimisticCount >= progress.total) setIsComplete(true);

                        // Save in background (don't await)
                        userService.saveScratch({
                            userPhone: userPhone,
                            userName: sessionStorage.getItem('userName') || 'Customer',
                            shopId: shopId,
                            shopName: progress.shopName,
                            reward: optimisticCount === progress.total ? progress.reward : { name: 'Stamp Collected', icon: '🎫' },
                            campaignName: progress.campaignName,
                            campaignMode: 'stamp' // explicitly track mode for dashboard accuracy
                        }).catch(err => console.error("Background save failed:", err));
                        
                        sessionStorage.setItem('lastStampTime', now.toString());
                    } else {
                        setStampData(progress);
                        if (progress.count >= progress.total) setIsComplete(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching progress:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgress();
    }, [shopId, userPhone, navigate]);

    const triggerConfetti = () => {
        confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#d4af37', '#000000', '#fcf6ba']
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#fdfdfd] flex flex-col items-center justify-center text-black">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="mb-8">
                    <Star size={40} className="text-[#d4af37]" />
                </motion.div>
                <p className="text-[10px] font-black uppercase tracking-[0.5em] animate-pulse">Syncing Stamp Protocol...</p>
            </div>
        );
    }

    const stamps = Array.from({ length: stampData?.total || 10 }, (_, i) => i + 1);

    return (
        <div className="min-h-screen bg-[#fdfdfd] relative overflow-hidden flex flex-col items-center p-6 pt-20">
            <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
            
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-xl z-10 space-y-10"
            >
                {/* Header */}
                <div className="text-center space-y-4">
                    <h1 className="text-5xl font-serif text-black tracking-tighter italic">
                        <span className="luxury-text">{stampData?.shopName}</span>
                    </h1>
                    <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-black text-white text-[9px] font-black tracking-[0.4em] uppercase shadow-xl">
                        <Star size={12} className="text-[#d4af37]" />
                        Loyalty Stamp Node
                    </div>
                </div>

                {/* Stamp Grid Container */}
                <div className="luxury-card p-10 md:p-12 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-black/[0.03]">
                        <motion.div 
                            className="h-full bg-[#d4af37]"
                            initial={{ width: 0 }}
                            animate={{ width: `${(stampData?.count / stampData?.total) * 100}%` }}
                        />
                    </div>

                    <div className="flex justify-between items-center mb-10">
                        <div>
                            <h3 className="text-xl font-serif text-black tracking-tight">Your Progress</h3>
                            <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest mt-1">
                                {stampData?.count} of {stampData?.total} Collected
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-2xl bg-black/5 flex items-center justify-center text-black">
                            <Zap size={20} />
                        </div>
                    </div>

                    {/* The Grid */}
                    <div className="grid grid-cols-5 gap-4">
                        {stamps.map((num) => {
                            const isCollected = num <= (stampData?.count || 0);
                            const isLast = num === stampData?.total;

                            return (
                                <motion.div
                                    key={num}
                                    initial={isCollected ? { scale: 0.8, opacity: 0 } : {}}
                                    animate={isCollected ? { scale: 1, opacity: 1 } : {}}
                                    className={`aspect-square rounded-2xl border flex items-center justify-center relative group transition-all duration-500 ${
                                        isCollected 
                                            ? 'bg-black border-black shadow-lg text-[#d4af37]' 
                                            : 'bg-black/[0.02] border-black/[0.05] text-black/10'
                                    }`}
                                >
                                    {isCollected ? (
                                        <CheckCircle2 size={24} />
                                    ) : isLast ? (
                                        <Gift size={20} className="opacity-50" />
                                    ) : (
                                        <span className="text-xs font-black">{num}</span>
                                    )}

                                    {num === (stampData?.count || 0) && (
                                        <motion.div 
                                            layoutId="active-stamp"
                                            className="absolute -inset-1 border-2 border-[#d4af37] rounded-2xl animate-pulse"
                                        />
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Status Message */}
                    <div className="mt-12 p-6 rounded-3xl bg-black/[0.02] border border-black/[0.05] text-center">
                        <AnimatePresence mode="wait">
                            {isComplete ? (
                                <motion.div 
                                    key="complete"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="space-y-4"
                                >
                                    <div className="w-16 h-16 bg-[#d4af37]/10 rounded-full flex items-center justify-center text-[#d4af37] mx-auto mb-4">
                                        <Crown size={32} />
                                    </div>
                                    <h4 className="text-2xl font-serif text-black italic">Victory Claimed!</h4>
                                    <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest leading-loose">
                                        You've unlocked: <br />
                                        <span className="text-black text-xs">{stampData?.reward?.name}</span>
                                    </p>
                                    <button 
                                        onClick={() => navigate('/reward')}
                                        className="luxury-button w-full flex items-center justify-center gap-3 !py-5 mt-4"
                                    >
                                        Claim Reward <ArrowRight size={18} />
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.div key="ongoing" className="space-y-2">
                                    <p className="text-[10px] font-bold text-black uppercase tracking-widest">
                                        Scan {stampData?.total - stampData?.count} more times
                                    </p>
                                    <p className="text-[9px] text-[#999] italic uppercase tracking-widest">
                                        to unlock your {stampData?.reward?.name}
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Footer Signals */}
                <div className="grid grid-cols-2 gap-6 opacity-30">
                    <div className="flex items-center gap-3 justify-center">
                        <ShieldCheck size={16} />
                        <span className="text-[8px] font-black uppercase tracking-widest">Verified Loyalty</span>
                    </div>
                    <div className="flex items-center gap-3 justify-center">
                        <Star size={16} fill="currentColor" />
                        <span className="text-[8px] font-black uppercase tracking-widest">Premium Member</span>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default StampPage;
